import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, CreditCard, Calculator, ServerCog, BarChart3, Inbox, Rocket, LogOut, Sparkles, Handshake, ExternalLink, Palette } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const FEATURED = ["Google Workspace", "HubSpot", "Calendly", "Semrush", "QuickBooks", "Brevo"];

export default function ProfileMenu() {
  const navigate = useNavigate();
  const { user, plan, isAdmin, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [partners, setPartners] = useState([]);
  const ref = useRef(null);

  useEffect(() => {
    let done = false;
    api.get("/vendors").then((res) => {
      if (done) return;
      const cat = res.data?.catalog || {};
      const all = [];
      Object.values(cat).forEach((c) => (c.vendors || []).forEach((v) => all.push({ ...v, category: c.label })));
      const picked = FEATURED.map((n) => all.find((v) => v.name === n)).filter(Boolean);
      setPartners((picked.length ? picked : all).slice(0, 6));
    }).catch(() => {});
    return () => { done = true; };
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const go = (path) => { setOpen(false); navigate(path); };

  const items = [
    plan === "free"
      ? { label: "Upgrade plan", icon: Sparkles, action: () => go("/pricing"), accent: true }
      : { label: "Manage subscription", icon: CreditCard, action: () => go("/subscription") },
    { label: "Savings & migration", icon: Calculator, action: () => go("/migration") },
    { label: "Why Emergent", icon: Rocket, action: () => go("/why-emergent") },
    ...((plan === "agency" || isAdmin) ? [{ label: "Backend scan", icon: ServerCog, action: () => go("/backend-scan") }] : []),
    ...((plan === "agency" || isAdmin) ? [{ label: "Report branding", icon: Palette, action: () => go("/branding") }] : []),
    ...(isAdmin ? [{ label: "Analytics", icon: BarChart3, action: () => go("/admin") }] : []),
    ...(isAdmin ? [{ label: "Leads", icon: Inbox, action: () => go("/admin/leads") }] : []),
  ];

  const initial = (user?.name || user?.email || "?").trim().charAt(0).toUpperCase();

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="profile-menu-btn"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 rounded-full pl-1 pr-2 py-1 hover:bg-white/5 transition-colors"
      >
        {user?.picture ? (
          <img src={user.picture} alt="" className="w-8 h-8 rounded-full border border-white/20 object-cover" referrerPolicy="no-referrer" />
        ) : (
          <span className="w-8 h-8 rounded-full grid place-items-center bg-[#007AFF]/20 border border-[#007AFF]/40 text-[#4da3ff] text-sm font-bold">{initial}</span>
        )}
        <ChevronDown size={14} className={`text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          data-testid="profile-menu"
          className="absolute right-0 mt-2 w-72 max-w-[calc(100vw-1.5rem)] rounded-2xl border border-white/10 bg-[#141414] shadow-2xl shadow-black/60 overflow-hidden z-50"
        >
          {/* Identity */}
          <div className="p-4 border-b border-white/10 flex items-center gap-3">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-11 h-11 rounded-full border border-white/20 object-cover" referrerPolicy="no-referrer" />
            ) : (
              <span className="w-11 h-11 rounded-full grid place-items-center bg-[#007AFF]/20 border border-[#007AFF]/40 text-[#4da3ff] text-lg font-bold">{initial}</span>
            )}
            <div className="min-w-0">
              <div className="text-white font-semibold text-sm truncate">{user?.name || "Your account"}</div>
              <div className="text-zinc-500 text-xs truncate">{user?.email}</div>
              <span className="inline-block mt-1 text-[10px] font-bold uppercase tracking-wider text-[#4da3ff] bg-[#007AFF]/10 border border-[#007AFF]/30 rounded-full px-2 py-0.5">
                {isAdmin ? "Admin" : plan} plan
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="p-1.5">
            {items.map((it) => (
              <button
                key={it.label}
                data-testid={`profile-item-${it.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                onClick={it.action}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  it.accent ? "text-[#4da3ff] hover:bg-[#007AFF]/10 font-medium" : "text-zinc-200 hover:bg-white/5"
                }`}
              >
                <it.icon size={16} /> {it.label}
              </button>
            ))}
          </div>

          {/* Referral partners */}
          {partners.length > 0 && (
            <div className="px-3 py-3 border-t border-white/10">
              <div className="flex items-center justify-between mb-2">
                <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-zinc-500">
                  <Handshake size={13} /> Partner tools
                </span>
                <button onClick={() => go("/migration")} data-testid="profile-see-all-partners" className="text-[11px] text-[#4da3ff] hover:text-white transition-colors">See all</button>
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                {partners.map((p) => (
                  <a
                    key={p.name}
                    href={p.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    data-testid={`profile-partner-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="group flex items-center justify-between gap-1 rounded-lg border border-white/10 bg-black/30 px-2.5 py-2 text-xs text-zinc-300 hover:border-[#4da3ff]/40 hover:text-white transition-colors"
                  >
                    <span className="truncate">{p.name}</span>
                    <ExternalLink size={11} className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                  </a>
                ))}
              </div>
            </div>
          )}

          <button
            data-testid="profile-logout-btn"
            onClick={() => { setOpen(false); logout(); }}
            className="w-full flex items-center gap-3 px-4 py-3 text-sm text-zinc-300 hover:bg-[#FF3B30]/10 hover:text-[#FF6961] transition-colors border-t border-white/10"
          >
            <LogOut size={16} /> Log out
          </button>
        </div>
      )}
    </div>
  );
}
